import { useId, useState } from "react";
import * as v from "valibot";
import {
  CONSENTIMENTO_CONTEUDO,
  CONSENTIMENTO_OFERTAS,
  InscricaoSchema,
} from "~/lib/lista/schema";

/**
 * A captura de e-mail — hoje a única coisa no site com função comercial.
 *
 * Pede só o e-mail: cada campo a mais derruba conversão, e o nome não é
 * necessário para entregar nada. As duas caixas de consentimento vêm
 * desmarcadas e separadas por finalidade, como manda a LGPD; a primeira é
 * obrigatória porque é a razão do cadastro, a segunda é outra finalidade e
 * fica à vontade.
 *
 * Não existe estado de "deu certo" que minta: a inscrição só vale depois do
 * clique no e-mail, então o sucesso aqui diz exatamente isso.
 */

type Estado =
  | { fase: "parado" }
  | { fase: "enviando" }
  | { fase: "olhe-o-email" }
  | { fase: "erro"; mensagem: string };

interface Props {
  /** De onde veio o visitante, para medir qual vídeo traz lista. */
  origem?: string;
}

export function EntreNaLista({ origem }: Props) {
  const idBase = useId();
  const idEmail = `${idBase}-email`;
  const idConteudo = `${idBase}-conteudo`;
  const idOfertas = `${idBase}-ofertas`;
  const idErro = `${idBase}-erro`;

  const [email, setEmail] = useState("");
  const [conteudo, setConteudo] = useState(false);
  const [ofertas, setOfertas] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [estado, setEstado] = useState<Estado>({ fase: "parado" });

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (estado.fase === "enviando") return;

    const corpo = {
      email,
      consenteConteudo: conteudo,
      consenteOfertas: ofertas,
      ...(origem ? { origem } : {}),
      honeypot,
    };

    // Mesma validação do servidor, só para o erro aparecer sem round-trip.
    const checado = v.safeParse(InscricaoSchema, corpo);
    if (!checado.success) {
      setEstado({
        fase: "erro",
        mensagem: checado.issues[0]?.message ?? "Confira os dados.",
      });
      return;
    }

    setEstado({ fase: "enviando" });
    try {
      const res = await fetch("/api/lista", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(corpo),
      });
      const dados = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        issues?: { message: string }[];
      };

      if (res.ok && dados.ok) {
        setEstado({ fase: "olhe-o-email" });
        return;
      }

      setEstado({
        fase: "erro",
        mensagem:
          dados.issues?.[0]?.message ??
          dados.message ??
          "Não consegui registrar agora. Tente de novo em alguns minutos.",
      });
    } catch {
      setEstado({
        fase: "erro",
        mensagem: "Sem conexão com o servidor. Tente de novo em alguns minutos.",
      });
    }
  }

  if (estado.fase === "olhe-o-email") {
    return (
      <div className="jg-lista-pronto" role="status">
        <p className="jg-display text-xl">Agora olhe seu e-mail.</p>
        <p className="mt-2 text-jg-fg-muted">
          Mandei um link de confirmação para <strong>{email}</strong>. Até você
          clicar nele, não entra nada na lista — e eu não te mando mais nada.
        </p>
        <p className="mt-2 text-sm text-jg-fg-muted">
          Não chegou em alguns minutos? Veja no spam, ou{" "}
          <button
            type="button"
            className="jg-link"
            onClick={() => setEstado({ fase: "parado" })}
          >
            tente outro endereço
          </button>
          .
        </p>
      </div>
    );
  }

  const enviando = estado.fase === "enviando";

  return (
    <form className="jg-lista" onSubmit={enviar} noValidate>
      <div className="jg-lista-linha">
        <label className="jg-lista-rotulo" htmlFor={idEmail}>
          Seu e-mail
        </label>
        <input
          id={idEmail}
          className="jg-campo"
          type="email"
          name="email"
          inputMode="email"
          autoComplete="email"
          placeholder="voce@exemplo.com"
          value={email}
          required
          disabled={enviando}
          aria-describedby={estado.fase === "erro" ? idErro : undefined}
          aria-invalid={estado.fase === "erro" ? true : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            if (estado.fase === "erro") setEstado({ fase: "parado" });
          }}
        />
        <button className="jg-button" type="submit" disabled={enviando}>
          {enviando ? "Enviando…" : "Quero receber"}
        </button>
      </div>

      {/* Armadilha de robô: fora da tela, mas não display:none, porque alguns
          preenchedores automáticos pulam campos escondidos desse jeito. */}
      <div className="jg-honeypot" aria-hidden="true">
        <label htmlFor={`${idBase}-hp`}>Deixe em branco</label>
        <input
          id={`${idBase}-hp`}
          type="text"
          name="assunto_extra"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <label className="jg-lista-caixa" htmlFor={idConteudo}>
        <input
          id={idConteudo}
          type="checkbox"
          checked={conteudo}
          disabled={enviando}
          onChange={(e) => {
            setConteudo(e.target.checked);
            if (estado.fase === "erro") setEstado({ fase: "parado" });
          }}
        />
        <span>{CONSENTIMENTO_CONTEUDO}</span>
      </label>

      <label className="jg-lista-caixa" htmlFor={idOfertas}>
        <input
          id={idOfertas}
          type="checkbox"
          checked={ofertas}
          disabled={enviando}
          onChange={(e) => setOfertas(e.target.checked)}
        />
        <span>
          {CONSENTIMENTO_OFERTAS}{" "}
          <span className="text-jg-fg-muted">(opcional)</span>
        </span>
      </label>

      {estado.fase === "erro" && (
        <p className="jg-lista-erro" id={idErro} role="alert">
          {estado.mensagem}
        </p>
      )}

      <p className="jg-lista-rodape">
        Duplo opt-in: você confirma por e-mail antes de entrar. Saída em um
        clique no rodapé de qualquer mensagem. O que eu faço com o endereço
        está na{" "}
        <a className="jg-link" href="/privacidade">
          política de privacidade
        </a>
        .
      </p>
    </form>
  );
}
